import { useState } from 'react';
import { useTicketCatalog } from '../core/_hooks';
import { departmentSubjectCount, filterCatalogBySearch, slaLabel } from '../core/_utils';

type Props = {
  onSelect: (subjectId: string) => void;
};

const TicketSubjectStep = ({ onSelect }: Props) => {
  const catalogQuery = useTicketCatalog();
  const [search, setSearch] = useState('');
  const [pickedDepartmentId, setPickedDepartmentId] = useState<string>();

  const catalog = catalogQuery.data ?? [];
  const departments = filterCatalogBySearch(catalog, search);
  const selectedDepartment = departments.find(({ id }) => id === pickedDepartmentId) ?? departments[0];

  return (
    <>
      <div className="d-none d-md-flex align-items-center gap-3 rounded bg-light-primary px-4 py-3 mb-6">
        <i className="ki-duotone ki-information-5 fs-2 text-primary">
          <span className="path1" />
          <span className="path2" />
          <span className="path3" />
        </i>
        <div className="d-flex flex-column flex-grow-1">
          <span className="text-gray-900 fw-bold fs-6">Escolha o assunto</span>
          <span className="text-gray-700 fs-7">
            Com o assunto certo, o chamado vai direto para a equipe que resolve. Em seguida você conta o que aconteceu.
          </span>
        </div>
        <span className="text-gray-600 fs-8 fw-semibold text-nowrap">Etapa 1 de 2</span>
      </div>
      <div className="mb-5">
        <label className="visually-hidden" htmlFor="ticket-subject-search">
          Buscar assunto
        </label>
        <input
          className="form-control form-control-solid"
          id="ticket-subject-search"
          placeholder="Busque pelo assunto: rebaixa, nota fiscal, impressora…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {departments.length === 0 ? (
        <div className="text-gray-600 fs-6 py-8 text-center">Nenhum assunto encontrado</div>
      ) : (
        <div className="row g-4">
          <div className="col-md-4 d-flex flex-column gap-1">
            <span className="text-gray-600 fw-bold fs-8 text-uppercase mb-1">Departamentos</span>
            {departments.map((department) => {
              const isSelected = department.id === selectedDepartment.id;
              const subjectCount = departmentSubjectCount(department.categories);
              const buttonClass = isSelected ? 'btn btn-light-primary fw-bold' : 'btn btn-light fw-semibold';

              return (
                <button
                  key={department.id}
                  aria-pressed={isSelected}
                  className={`${buttonClass} d-flex justify-content-between align-items-center text-start`}
                  type="button"
                  onClick={() => setPickedDepartmentId(department.id)}
                >
                  <span>{department.name}</span>
                  <span className="text-gray-600 fs-8">{subjectCount}</span>
                </button>
              );
            })}
          </div>
          <div className="col-md-8 d-flex flex-column gap-2">
            <span className="text-gray-600 fw-bold fs-8 text-uppercase mb-1">
              Assuntos de {selectedDepartment.name}
            </span>
            {selectedDepartment.categories.map((category) => (
              <div key={category.id} className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2 text-gray-700 fw-bold fs-7 mt-2">
                  <i className="bi bi-folder2 text-gray-600" />
                  {category.name}
                </div>
                {category.subjects.map((subject) => {
                  const slaHoursLabel = slaLabel(subject.sla);
                  const responseLabel = `responde em ${slaHoursLabel} úteis`;

                  return (
                    <button
                      key={subject.id}
                      className="d-flex align-items-center gap-3 rounded border bg-transparent p-3 text-start"
                      type="button"
                      onClick={() => onSelect(subject.id)}
                    >
                      <span className="flex-grow-1">
                        <span className="d-block fw-bold text-gray-800 fs-6">{subject.name}</span>
                        <span className="d-block text-gray-600 fs-7">{subject.hint}</span>
                      </span>
                      <span className="text-gray-600 fs-8 text-nowrap">{responseLabel}</span>
                      <i className="bi bi-chevron-right text-gray-600" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TicketSubjectStep;
